import * as THREE from 'three';
import { EFFECT_LAYER } from '../enhanced-effects';
import { SUN_DIR } from './sky-dome';

// ---------------------------------------------------------------------------
// 2. Dust Particles — floating sand/dust motes with sunlit sparkle
// ---------------------------------------------------------------------------

const PARTICLE_COUNT = 3000;
const BOX_SIZE = new THREE.Vector3(80, 30, 80);
const WIND_SPEED = 0.3; // units/sec in X direction

export function createDustParticles(scene: THREE.Scene): {
  points: THREE.Points;
  update: (dt: number, camPos: THREE.Vector3) => void;
  dispose: () => void;
} {
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const sizes = new Float32Array(PARTICLE_COUNT);
  const alphas = new Float32Array(PARTICLE_COUNT);
  const colorTypes = new Float32Array(PARTICLE_COUNT);   // color variation per particle
  const heightLayers = new Float32Array(PARTICLE_COUNT);  // wind layer assignment
  const randomDrift = new Float32Array(PARTICLE_COUNT * 3);

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * BOX_SIZE.x;
    positions[i * 3 + 1] = Math.random() * BOX_SIZE.y;
    positions[i * 3 + 2] = (Math.random() - 0.5) * BOX_SIZE.z;

    // Size variety: mostly small motes, some larger floating debris
    const sizeRoll = Math.random();
    if (sizeRoll < 0.7) {
      sizes[i] = 0.05 + Math.random() * 0.06;      // small motes (0.05-0.11)
    } else if (sizeRoll < 0.92) {
      sizes[i] = 0.12 + Math.random() * 0.10;      // medium particles (0.12-0.22)
    } else {
      sizes[i] = 0.24 + Math.random() * 0.16;      // larger debris (0.24-0.40)
    }

    alphas[i] = 0.12 + Math.random() * 0.38;

    // Color type: 0=warm gold, 1=pale white, 2=darker sandy
    colorTypes[i] = Math.random();

    // Height-based wind layer
    const normalizedHeight = positions[i * 3 + 1] / BOX_SIZE.y;
    heightLayers[i] = normalizedHeight;

    // Random drift per particle — varies by height layer
    const windMultiplier = 0.6 + normalizedHeight * 0.8; // faster wind higher up
    randomDrift[i * 3] = (Math.random() - 0.5) * 0.12 * windMultiplier;
    randomDrift[i * 3 + 1] = (Math.random() - 0.5) * 0.04;
    randomDrift[i * 3 + 2] = (Math.random() - 0.5) * 0.10 * windMultiplier;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
  geo.setAttribute('aAlpha', new THREE.BufferAttribute(alphas, 1));
  geo.setAttribute('aColorType', new THREE.BufferAttribute(colorTypes, 1));
  geo.setAttribute('aHeightLayer', new THREE.BufferAttribute(heightLayers, 1));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    uniforms: {
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSunDir: { value: SUN_DIR.clone() },
      uTime: { value: 0.0 },
    },
    vertexShader: /* glsl */ `
      attribute float aSize;
      attribute float aAlpha;
      attribute float aColorType;
      attribute float aHeightLayer;
      varying float vAlpha;
      varying float vColorType;
      varying float vSunFactor;
      uniform float uPixelRatio;
      uniform vec3 uSunDir;
      uniform float uTime;
      void main() {
        vAlpha = aAlpha;
        vColorType = aColorType;
        vec4 mvPos = modelViewMatrix * vec4(position, 1.0);

        // Sunlit sparkle: particles whose position aligns with sun direction sparkle
        vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
        vec3 toParticle = normalize(worldPos);
        float sunAlign = max(dot(toParticle, uSunDir), 0.0);
        // Sparkle: high-frequency oscillation modulated by sun alignment
        float sparkle = pow(sunAlign, 4.0) * (0.5 + 0.5 * sin(uTime * 8.0 + position.x * 10.0 + position.z * 7.0));
        vSunFactor = sparkle;

        gl_PointSize = aSize * uPixelRatio * (300.0 / -mvPos.z);
        // Larger max for debris particles
        gl_PointSize = clamp(gl_PointSize, 1.0, 12.0);
        gl_Position = projectionMatrix * mvPos;
      }
    `,
    fragmentShader: /* glsl */ `
      varying float vAlpha;
      varying float vColorType;
      varying float vSunFactor;
      void main() {
        // Soft circle
        float d = length(gl_PointCoord - 0.5) * 2.0;
        float alpha = smoothstep(1.0, 0.3, d) * vAlpha;

        // Color variety: warm gold, pale white, darker sandy
        vec3 warmGold  = vec3(0.90, 0.80, 0.55);
        vec3 paleWhite = vec3(0.92, 0.90, 0.85);
        vec3 darkSandy = vec3(0.70, 0.62, 0.48);

        vec3 color;
        if (vColorType < 0.45) {
          color = warmGold;
        } else if (vColorType < 0.78) {
          color = paleWhite;
        } else {
          color = darkSandy;
        }

        // Sunlit sparkle: brighten and boost alpha for sun-catching particles
        color += vec3(0.3, 0.25, 0.15) * vSunFactor;
        alpha += vSunFactor * 0.3;

        gl_FragColor = vec4(color, alpha);
      }
    `,
  });

  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  points.layers.set(EFFECT_LAYER);
  scene.add(points);

  // Track center offset
  const center = new THREE.Vector3();

  return {
    points,
    update(dt: number, camPos: THREE.Vector3) {
      center.copy(camPos);
      if (!mat.uniforms?.uTime) return;
      (mat.uniforms.uTime as { value: number }).value += dt;

      const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
      const arr = posAttr.array as Float32Array;
      const halfX = BOX_SIZE.x * 0.5;
      const halfZ = BOX_SIZE.z * 0.5;
      const maxY = BOX_SIZE.y;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const ix = i * 3, iy = i * 3 + 1, iz = i * 3 + 2;

        // Height-based wind speed variation
        const heightFactor = 0.6 + heightLayers[i] * 0.8;

        // Wind + random drift, scaled by height layer
        arr[ix] += (WIND_SPEED * heightFactor + randomDrift[ix]) * dt;
        arr[iy] += randomDrift[iy] * dt;
        arr[iz] += (randomDrift[iz] * heightFactor) * dt;

        // Wrap around camera-centered box
        const relX = arr[ix] - center.x;
        const relZ = arr[iz] - center.z;

        if (relX > halfX) arr[ix] -= BOX_SIZE.x;
        else if (relX < -halfX) arr[ix] += BOX_SIZE.x;
        if (relZ > halfZ) arr[iz] -= BOX_SIZE.z;
        else if (relZ < -halfZ) arr[iz] += BOX_SIZE.z;
        if (arr[iy] > maxY) arr[iy] -= maxY;
        else if (arr[iy] < 0) arr[iy] += maxY;
      }
      posAttr.needsUpdate = true;
    },
    dispose() {
      scene.remove(points);
      geo.dispose();
      mat.dispose();
    },
  };
}
